import React, { useEffect, useState } from 'react';
import { getExpiring, getReorder, getAuditSummary } from '../services/api';
import '../App.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://hlad.pycube.com/api';

export default function ReportsPage({ labId }) {
  const [tab, setTab]             = useState('reorder');
  const [reorder, setReorder]     = useState([]);
  const [expiring, setExpiring]   = useState([]);
  const [auditLog, setAuditLog]   = useState([]);
  const [days, setDays]           = useState(30);
  const [loading, setLoading]     = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([getReorder(labId), getExpiring(days, labId), getAuditSummary(20, labId)])
      .then(([r, e, a]) => { setReorder(r.data); setExpiring(e.data); setAuditLog(a.data); })
      .finally(() => setLoading(false));
  }, [days, labId]);

  const downloadReorderExcel = async () => {
    setDownloading(true);
    try {
      const url = `${API_BASE}/reports/reorder-excel${labId ? `?lab_id=${labId}` : ''}`;
      const resp = await fetch(url);
      if (!resp.ok) { alert('No items need reordering right now.'); return; }
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `reorder-${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { alert('Could not download report.'); }
    finally { setDownloading(false); }
  };

  const tabs = [
    { id: 'reorder',  label: '📦 Reorder Report' },
    { id: 'expiring', label: '📅 Expiry Report' },
    { id: 'audits',   label: '📋 Audit History' },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Reports</div>
          <div className="page-sub">Export and view stock reports</div>
        </div>
        <button
          onClick={downloadReorderExcel}
          disabled={downloading}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 20px', borderRadius: 10, border: 'none',
            background: '#0A2A5E', color: '#fff', fontSize: 14,
            fontWeight: 700, cursor: downloading ? 'default' : 'pointer',
            opacity: downloading ? 0.7 : 1,
          }}
        >
          {downloading ? '⏳ Generating…' : '⬇ Download Reorder List (.xlsx)'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '2px solid #e2e8f0', paddingBottom: 0 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '10px 18px', border: 'none', background: 'none', cursor: 'pointer',
            fontSize: 14, fontWeight: 600,
            color: tab === t.id ? '#0A2A5E' : '#94a3b8',
            borderBottom: tab === t.id ? '2px solid #0A2A5E' : '2px solid transparent',
            marginBottom: -2,
          }}>{t.label}</button>
        ))}
      </div>

      {loading ? <div className="loading">Loading…</div> : (
        <>
          {/* ── Reorder ── */}
          {tab === 'reorder' && (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ fontWeight: 700, color: '#0A2A5E' }}>Items to Reorder ({reorder.length})</div>
                <button className="btn btn-outline btn-sm" onClick={() => {
                  if (!reorder.length) return;
                  const keys = Object.keys(reorder[0]).filter(k => !['id','section_id','storage_location_id','is_active'].includes(k));
                  const csv = [keys.join(','), ...reorder.map(r => keys.map(k => JSON.stringify(r[k]??'')).join(','))].join('\n');
                  const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv], {type:'text/csv'})); a.download = 'reorder-report.csv'; a.click();
                }}>⬇ Export CSV</button>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Item</th><th>Section</th><th>Location</th><th>Current Qty</th><th>Hard Threshold</th><th>Reorder Qty</th><th>Vendor</th><th>Cat #</th></tr>
                  </thead>
                  <tbody>
                    {reorder.length === 0 ? (
                      <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>No items need reordering 🎉</td></tr>
                    ) : reorder.map(item => (
                      <tr key={item.id}>
                        <td style={{ fontWeight: 600 }}>{item.name}</td>
                        <td>{item.section_name}</td>
                        <td>{item.location_name || '—'}</td>
                        <td><span style={{ color: '#dc2626', fontWeight: 700 }}>{item.current_qty}</span> {item.unit}</td>
                        <td>{item.hard_threshold} {item.unit}</td>
                        <td>{item.reorder_qty ? `${item.reorder_qty} ${item.unit}` : '—'}</td>
                        <td>{item.vendor || '—'}</td>
                        <td>{item.catalog_number || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Expiry ── */}
          {tab === 'expiring' && (
            <div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>Show expiring within:</span>
                {[7, 14, 30, 60, 90].map(d => (
                  <button key={d} onClick={() => setDays(d)} className="btn btn-sm" style={{ background: days === d ? '#0A2A5E' : 'white', color: days === d ? 'white' : '#374151', border: '1.5px solid #e2e8f0' }}>
                    {d}d
                  </button>
                ))}
                <button className="btn btn-outline btn-sm" onClick={() => {
                  if (!expiring.length) return;
                  const keys = Object.keys(expiring[0]).filter(k => !['id','section_id','storage_location_id','is_active'].includes(k));
                  const csv = [keys.join(','), ...expiring.map(r => keys.map(k => JSON.stringify(r[k]??'')).join(','))].join('\n');
                  const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv], {type:'text/csv'})); a.download = `expiry-report-${days}d.csv`; a.click();
                }}>⬇ Export CSV</button>
              </div>
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr><th>Item</th><th>Section</th><th>Lot #</th><th>Expiry Date</th><th>Days Left</th><th>Qty</th></tr>
                    </thead>
                    <tbody>
                      {expiring.length === 0 ? (
                        <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>No items expiring within {days} days 🎉</td></tr>
                      ) : expiring.map((lot, i) => {
                        const daysLeft = lot.days_left != null ? lot.days_left : Math.ceil((new Date(lot.expiry_date) - new Date()) / 86400000);
                        const color = daysLeft <= 0 ? '#dc2626' : daysLeft <= 7 ? '#dc2626' : daysLeft <= 14 ? '#d97706' : '#374151';
                        const label = daysLeft <= 0 ? `Expired ${Math.abs(daysLeft)}d ago` : daysLeft === 0 ? 'TODAY' : `${daysLeft}d`;
                        return (
                          <tr key={i}>
                            <td style={{ fontWeight: 600 }}>{lot.item_name}</td>
                            <td>{lot.section_name}</td>
                            <td><code>{lot.lot_number || '—'}</code></td>
                            <td>{lot.expiry_date?.slice(0,10)}</td>
                            <td>
                              <span style={{ fontWeight: 700, color }}>
                                {label}
                              </span>
                            </td>
                            <td>{lot.qty}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ── Audit History ── */}
          {tab === 'audits' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: '#64748b' }}>Last {auditLog.length} submitted audits</div>
                <button className="btn btn-outline btn-sm" onClick={() => {
                  if (!auditLog.length) return;
                  const keys = Object.keys(auditLog[0]).filter(k => !['id','section_id','storage_location_id','is_active'].includes(k));
                  const csv = [keys.join(','), ...auditLog.map(r => keys.map(k => JSON.stringify(r[k]??'')).join(','))].join('\n');
                  const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv], {type:'text/csv'})); a.download = 'audit-history.csv'; a.click();
                }}>⬇ Export CSV</button>
              </div>
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Section</th>
                        <th>Type</th>
                        <th>By</th>
                        <th>Items</th>
                        <th style={{ color: '#dc2626' }}>⬇ Decreased</th>
                        <th style={{ color: '#16a34a' }}>⬆ Increased</th>
                        <th>Unchanged</th>
                        <th style={{ color: '#dc2626' }}>🔴 Critical</th>
                        <th style={{ color: '#d97706' }}>🟡 Low</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLog.length === 0 ? (
                        <tr><td colSpan={10} style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>No audits submitted yet</td></tr>
                      ) : auditLog.map(a => (
                        <tr key={a.id}>
                          <td style={{ fontSize: 12 }}>{new Date(a.started_at).toLocaleDateString()}</td>
                          <td>{a.section_name}</td>
                          <td><span className={`badge ${a.audit_type === 'receiving' ? 'badge-blue' : ''}`}>{a.audit_type}</span></td>
                          <td>{a.conducted_by || '—'}</td>
                          <td><b>{a.items_counted}</b></td>
                          <td style={{ color: '#dc2626', fontWeight: 700 }}>{a.decreased}</td>
                          <td style={{ color: '#16a34a', fontWeight: 700 }}>{a.increased}</td>
                          <td style={{ color: '#94a3b8' }}>{a.unchanged}</td>
                          <td>
                            {a.critical_items > 0
                              ? <span className="badge badge-critical">{a.critical_items}</span>
                              : <span style={{ color: '#94a3b8' }}>0</span>}
                          </td>
                          <td>
                            {a.low_items > 0
                              ? <span className="badge badge-low">{a.low_items}</span>
                              : <span style={{ color: '#94a3b8' }}>0</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Summary cards */}
              {auditLog.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginTop: 20 }}>
                  <div className="card" style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 28, fontWeight: 800, color: '#0A2A5E' }}>{auditLog.length}</div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Total Audits</div>
                  </div>
                  <div className="card" style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 28, fontWeight: 800, color: '#16a34a' }}>
                      {Math.round(auditLog.reduce((s,a) => s + parseInt(a.items_counted||0), 0) / auditLog.length)}
                    </div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Avg Items / Audit</div>
                  </div>
                  <div className="card" style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 28, fontWeight: 800, color: '#dc2626' }}>
                      {auditLog.reduce((s,a) => s + parseInt(a.critical_items||0), 0)}
                    </div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Total Critical Flags</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
